import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogFooter } from "@/components/ui/dialog";
import { DialogTitleWithBug } from "@/components/ui/dialog-title-with-bug";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useBusinessMetricsMutations } from "@/hooks/useBusinessMetrics";
import { useBrandProductsHooks } from "@/hooks/useBrandProductsHooks";
import { useMetricTerminology } from "@/hooks/useMetricTerminology";
import { format } from "date-fns";
import { AlertCircle, Loader2 } from "lucide-react";

interface BusinessMetricsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  brandId: string;
  editingMetric?: any;
}

interface FormErrors {
  date?: string;
  type?: string;
  quantity?: string;
  amount?: string;
  content?: string;
}

export const BusinessMetricsDialog: React.FC<BusinessMetricsDialogProps> = ({
  open,
  onOpenChange,
  brandId,
  editingMetric
}) => {
  const { createMetric, updateMetric } = useBusinessMetricsMutations(brandId);
  const { data: products } = useBrandProductsHooks(brandId);
  const { getAllMetricTypes } = useMetricTerminology();
  
  const [formData, setFormData] = useState({
    date: '',
    type: '',
    source: '',
    referrer: '',
    related_product: '',
    quantity: '',
    amount: '',
    url: '',
    content: '',
    medium: '',
    channel: ''
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (editingMetric) {
      setFormData({
        date: format(new Date(editingMetric.date), 'yyyy-MM-dd'),
        type: editingMetric.type || '',
        source: editingMetric.source || '',
        referrer: editingMetric.referrer || '',
        related_product: editingMetric.related_product || '',
        quantity: editingMetric.quantity?.toString() || '1',
        amount: editingMetric.amount?.toString() || '0',
        url: editingMetric.url || '',
        content: editingMetric.content ? JSON.stringify(editingMetric.content, null, 2) : '',
        medium: editingMetric.medium || '',
        channel: editingMetric.channel || ''
      });
    } else {
      setFormData({
        date: format(new Date(), 'yyyy-MM-dd'),
        type: '',
        source: '',
        referrer: '',
        related_product: '',
        quantity: '1',
        amount: '0',
        url: '',
        content: '',
        medium: '',
        channel: ''
      });
    }
    setErrors({});
  }, [editingMetric, open]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.date) {
      newErrors.date = 'Date is required';
    }

    if (!formData.type) {
      newErrors.type = 'Type is required';
    }

    if (!formData.quantity || isNaN(Number(formData.quantity)) || Number(formData.quantity) <= 0) {
      newErrors.quantity = 'Quantity must be a positive number';
    }

    if (!formData.amount || isNaN(Number(formData.amount)) || Number(formData.amount) < 0) {
      newErrors.amount = 'Amount must be a non-negative number';
    }

    // Validate JSON content if provided
    if (formData.content.trim()) {
      try {
        JSON.parse(formData.content);
      } catch (e) {
        newErrors.content = 'Content must be valid JSON format';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      let contentJson = null;
      if (formData.content.trim()) {
        contentJson = JSON.parse(formData.content);
      }

      const metricData = {
        brand_id: brandId,
        date: formData.date,
        type: formData.type,
        source: formData.source || null,
        referrer: formData.referrer || null,
        related_product: formData.related_product || null,
        quantity: parseInt(formData.quantity),
        amount: parseFloat(formData.amount),
        url: formData.url || null,
        content: contentJson,
        medium: formData.medium || null,
        channel: formData.channel || null
      };

      if (editingMetric) {
        await updateMetric.mutateAsync({
          id: editingMetric.id,
          ...metricData
        });
      } else {
        await createMetric.mutateAsync(metricData);
      }
      
      onOpenChange(false);
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] h-[80vh] flex flex-col mx-4 my-8">
        <DialogHeader>
          <DialogTitleWithBug 
            popupName={editingMetric ? "Edit Business Metric" : "Create Business Metric"}
            popupContext="/business-metrics"
            isOpen={open}
          >
            {editingMetric ? "Edit Business Metric" : "Create Business Metric"}
          </DialogTitleWithBug>
        </DialogHeader>
        
        <ScrollArea className="flex-1 h-full">
          <div className="space-y-4 pb-4 px-6">
            {/* Show validation errors at top */}
            {Object.keys(errors).length > 0 && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Please fix the following errors:
                  <ul className="mt-2 list-disc list-inside">
                    {Object.entries(errors).map(([field, error]) => (
                      <li key={field}>{error}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            <form id="business-metric-form" onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="date">Date *</Label>
                  <Input
                    id="date"
                    type="date"
                    value={formData.date}
                    onChange={(e) => handleInputChange('date', e.target.value)}
                    className={errors.date ? 'border-red-500' : ''}
                    required
                  />
                  {errors.date && <p className="text-sm text-red-500">{errors.date}</p>}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="type">Type *</Label>
                  <Select value={formData.type} onValueChange={(value) => handleInputChange('type', value)}>
                    <SelectTrigger className={errors.type ? 'border-red-500' : ''}>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent className="bg-background border z-[9999]" position="popper" sideOffset={4}>
                      {getAllMetricTypes().map((type) => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.type && <p className="text-sm text-red-500">{errors.type}</p>}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="source">Source</Label>
                  <Input
                    id="source"
                    value={formData.source}
                    onChange={(e) => handleInputChange('source', e.target.value)}
                    placeholder="e.g., Google Ads, Facebook"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="referrer">Referrer</Label>
                  <Input
                    id="referrer"
                    value={formData.referrer}
                    onChange={(e) => handleInputChange('referrer', e.target.value)}
                    placeholder="e.g., organic, paid"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="medium">Medium</Label>
                  <Input
                    id="medium"
                    value={formData.medium}
                    onChange={(e) => handleInputChange('medium', e.target.value)}
                    placeholder="e.g., organic, paid, social, email"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="channel">Channel</Label>
                  <Input
                    id="channel"
                    value={formData.channel}
                    onChange={(e) => handleInputChange('channel', e.target.value)}
                    placeholder="e.g., facebook, instagram, whatsapp"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="url">URL</Label>
                <Input
                  id="url"
                  type="url"
                  value={formData.url}
                  onChange={(e) => handleInputChange('url', e.target.value)}
                  placeholder="https://example.com/landing-page"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="related_product">Related Product</Label>
                <Select value={formData.related_product || 'none'} onValueChange={(value) => handleInputChange('related_product', value === 'none' ? '' : value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a product (optional)" />
                  </SelectTrigger>
                  <SelectContent className="bg-background border z-[9999]" position="popper" sideOffset={4}>
                    <SelectItem value="none">No product selected</SelectItem>
                    {products?.map((product) => (
                      <SelectItem key={product.id} value={product.id}>
                        {product.product}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="quantity">Quantity *</Label>
                  <Input
                    id="quantity"
                    type="number"
                    min="1"
                    value={formData.quantity}
                    onChange={(e) => handleInputChange('quantity', e.target.value)}
                    className={errors.quantity ? 'border-red-500' : ''}
                    placeholder="1"
                  />
                  {errors.quantity && <p className="text-sm text-red-500">{errors.quantity}</p>}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="amount">Amount *</Label>
                  <Input
                    id="amount"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => handleInputChange('amount', e.target.value)}
                    className={errors.amount ? 'border-red-500' : ''}
                    placeholder="0.00"
                  />
                  {errors.amount && <p className="text-sm text-red-500">{errors.amount}</p>}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="content">Content (JSON)</Label>
                <Textarea
                  id="content"
                  value={formData.content}
                  onChange={(e) => handleInputChange('content', e.target.value)}
                  placeholder='{"name": "John Doe", "email": "john@example.com"}'
                  className={`min-h-[100px] font-mono text-sm ${errors.content ? 'border-red-500' : ''}`}
                />
                {errors.content && <p className="text-sm text-red-500">{errors.content}</p>}
                <p className="text-xs text-muted-foreground">
                  Optional JSON data associated with this metric
                </p>
              </div>
            </form>
          </div>
        </ScrollArea>
        
        <DialogFooter className="gap-2">
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            form="business-metric-form"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {editingMetric ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              editingMetric ? 'Update Metric' : 'Create Metric'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};